"use strict";

const Contentstack = require('contentstack');
let env = null;
let Stack = null;

const {contentstack: devEnvConfig} = require(`../config/dev.json`);
const {contentstack: testEnvConfig} = require(`../config/test.json`);
const {contentstack: stageEnvConfig} = require(`../config/stage.json`);
const {contentstack: prodEnvConfig} = require(`../config/prod.json`);

/**
 * Gets the correct contentstack config settings
 * @param {string} env
 * @returns {object}
 */
function getConfig(env) {
  let config = {};

  switch (env) {
    case 'dev': {
      config = devEnvConfig;
      break;
    }
    case 'test': {
      config = testEnvConfig;
      break;
    }
    case 'stage': {
      config = stageEnvConfig;
      break;
    }
    case 'prod': {
      config = prodEnvConfig;
      break;
    }
  }

  return config;
}

/**
 * Convert string from snake case to camel case
 * @param {string} snakeCasedString
 * @returns {string} camelCaseString
 * @private
 */
function _snakeToCamel(s) {
  return s.replace(/(_\w)/g, k => k[1].toUpperCase());
}

/**
 * Convert object with snake case properties to camel case
 * @param {*} obj - object to convert
 * @returns {*} camelCasedObject
 * @private
 */
function _snakeObjectToCamel(obj) {
  // If obj is falsy or not an object, return it unchanged
  if (!obj || typeof obj !== 'object') return obj;

  if (Array.isArray(obj)) {
    // obj is array; recurse into its children
    return obj.map(_snakeObjectToCamel);
  } else {
    // obj is object; convert its keys and recurse into its values
    const camelKeyedFields = Object.entries(obj).map(([k, v]) => [
      _snakeToCamel(k),
      _snakeObjectToCamel(v)
    ]);

    return Object.fromEntries(camelKeyedFields);
  }
}

/**
 * Get the environment from the url if we are running in a browser
 * @return {string} environment
 * @private
 */
function _getEnv() {
  return (window.location.hostname.includes('dev') || window.location.hostname.includes('localhost')) ? 'dev' :
    window.location.hostname.includes('test') ? 'test' :
      window.location.hostname.includes('stage') ? 'stage' : 'prod';
}

const liftSubsections = (strings) => {
  let retStrings = {};
  Object.keys(strings).forEach((key) => {
    if (typeof strings[key] === 'string') {
      retStrings[key] = strings[key];
    }
    if (typeof strings[key] === 'object' && !Array.isArray(strings[key])) {
      const subStrings = liftSubsections(strings[key]);
      retStrings = Object.assign(retStrings, subStrings);
    }
  });

  return retStrings;
};

/**
 * @deprecated
 * getStrings for application
 * @param {string[]} options.contentTypes - list of content types that contains the strings for your application
 * @param {string} options.language - language you want to get the translations for
 * @param {string=} options.country
 * @param {boolean=} options.merge - If true results are merged into a single object.  Otherwise the object contains each contentType.
 * @return {Promise<unknown[]|*|undefined>} resultStrings
 */
async function getStrings(options) {
  const {contentTypes, language, country, merge} = options;
  if (!contentTypes || !contentTypes.length || !language) {
    throw new Error('Call to getStrings: options.contentTypes and language are required');
  }
  const mappedLanguage = language === 'in' ? 'id' : language;
  const locale = country ? `${country}-${mappedLanguage}` : mappedLanguage;

  try {
    const promises = contentTypes.map(contentType => {
      const Query = Stack.ContentType(contentType).Query();
      return Query.language(locale).includeFallback().toJSON().find();
    });
    const results = await Promise.all(promises);
    const r = results.flat().flat();

    if (merge) {
      const merged = r.reduce((result, current) => {
        return Object.assign(result, _snakeObjectToCamel(current));
      }, {});
      return liftSubsections(merged);
    } else {
      const stringTypes = {};
      r.forEach((cntType, index) => {
        stringTypes[_snakeToCamel(contentTypes[index])] = liftSubsections(_snakeObjectToCamel(r[index]));
      });
      return stringTypes;
    }
  } catch (e) {
    console.log(e);
    if (country) {
      return getStrings({contentTypes, language, merge});
    }
  }
}

/**
 * Get entries for a list of content types.  The content types must be of type single.
 * Entries are returned in the order they were requested
 * This function is useful for getting a list of strings from multiple content types at once.
 *
 * @param {string[]} options.contentTypeUIDs - list of content types that contains the strings for your application
 * @param {string} options.language - language you want to get the translations for
 * @param {string=} options.country
 * @param {boolean=} options.camelcase
 * @returns {Promise<unknown[]>} The entries are returned in the order they were requested
 */
async function getSingletonEntries(options) {
  const {contentTypeUIDs, language, country, camelcase} = options;
  if (!contentTypeUIDs || !contentTypeUIDs.length || !language) {
    throw new Error('Call to getSingletonEntries: options.contentTypeUIDs and language are required');
  }
  const mappedLanguage = language === 'in' ? 'id' : language;
  const locale = country ? `${country}-${mappedLanguage}` : mappedLanguage;

  try {
    const promises = contentTypeUIDs.map(uid => {
      return Stack
        .ContentType(uid)
        .Query()
        .language(locale)
        .includeFallback()
        .toJSON()
        .findOne();
    });
    const result = await Promise.all(promises);
    return camelcase ? result.map(r => _snakeObjectToCamel(r)) : result;
  } catch (e) {
    console.error(e);
    return [];
  }
}

/**
 * Returns contentstack Stack object initialized with the api key, deliveryToken, and extended with custom functionality
 * @param {'dev' | 'test' | 'stage' | 'prod'} environment
 * @return {Object} Stack - contentstack Stack sdk object with additional getStrings function
 */
function getStack(environment) {
  if (!environment) {
    if (!window || !window.location) {
      throw new Error('Environment must be passed into getStack');
    }
    environment = _getEnv();
  }

  // If the environment requested hasn't changed return the stack we already have
  if (environment === env && Stack) {
    return Stack;
  }

  env = environment;
  const envConfig = getConfig(env);
  Stack = Contentstack.Stack(envConfig.apiKey, envConfig.deliveryToken, envConfig.environment);

  // extend the Stack with a custom function to get translations
  Stack.getStrings = getStrings;
  Stack.getSingletonEntries = getSingletonEntries;

  return Stack;
}

module.exports = {
  getStack
};
